import { useState } from 'react';

import { WidgetRender } from './WidgetRender.js';

/**
 * A widget, drawn as it will appear on stream (ARCHITECTURE.md §103.2).
 *
 * ==================== IT IS THE REAL RENDERER ====================
 * `WidgetRender` is the same component the browser source runs. A preview
 * built from a separate mock would be a drawing of a widget rather than the
 * widget, and the two would drift the first time either changed. What an
 * operator sees here is what OBS will draw.
 * ================================================================
 *
 * ==================== WHY THE BACKDROP MATTERS ====================
 * A widget is transparent by default and is composited over gameplay. Judging
 * white text on Connaro's dark Console is judging it against a background it
 * will never have — so the backdrop is switchable, and the checkerboard is the
 * honest default because it shows transparency as transparency.
 *
 * The backdrop is a PREVIEW surface and never part of the widget: nothing
 * here reaches the configuration, and the browser source has no backdrop at
 * all (§103.6).
 * ==================================================================
 */

export const BACKDROPS = ['grid', 'dark', 'light', 'scene'] as const;
export type Backdrop = (typeof BACKDROPS)[number];

const BACKDROP_LABELS: Record<Backdrop, string> = {
  grid: 'Transparent',
  dark: 'Dark',
  light: 'Light',
  scene: 'Gameplay',
};

/**
 * Preview data, marked as preview data.
 *
 * §103.12: a simulated event must never reach persistence, Alerts, Bot,
 * Moderator or a goal's real progress. These values exist only in this
 * component's props — there is no code path from here to the API at all,
 * which is a stronger guarantee than a flag would be.
 *
 * The names are deliberately awkward rather than tidy: a long one, a short
 * one, and one with no avatar, so a layout that only works for "Viewer123"
 * fails here rather than on somebody's stream.
 */
export function previewState(widgetType: string): Record<string, unknown> {
  const now = new Date().toISOString();

  if (widgetType.endsWith('GOAL')) {
    return { currentValue: 742, startingValue: 500, target: 1000 };
  }

  if (widgetType === 'CHAT_OVERLAY') {
    /*
     * Deliberately awkward, and now deliberately VARIED.
     *
     * A preview built from three tidy medium-length lines proves only that
     * three tidy lines fit. This exercises the cases that actually break a
     * layout on somebody's stream: a handle far longer than the box, a
     * two-character message, an unbroken string with no spaces to wrap at,
     * badges of different widths, and one line with an avatar where the rest
     * have none — which is the real mixture, because avatars arrive on live
     * messages and never on replayed ones (§103.13).
     */
    return {
      messages: [
        {
          id: 'p1', platform: 'twitch', displayName: 'Kestrel',
          text: 'first time catching you live',
          badges: [{ id: 'moderator', label: 'MOD' }],
          avatarUrl: null, occurredAt: now,
        },
        {
          id: 'p2', platform: 'kick', displayName: 'a_very_long_viewer_handle_indeed',
          text: 'that last round was genuinely unreal, how did you survive that',
          badges: [], avatarUrl: null, occurredAt: now,
        },
        {
          id: 'p3', platform: 'youtube', displayName: 'Mo', text: 'gg',
          badges: [{ id: 'member', label: 'MEMBER' }],
          avatarUrl: null, occurredAt: now,
        },
        {
          id: 'p4', platform: 'twitch', displayName: 'ぷらいむ',
          text: 'https://example.com/a-link-with-no-spaces-in-it-at-all-to-wrap-on',
          badges: [], avatarUrl: null, occurredAt: now,
        },
      ],
    };
  }

  if (widgetType === 'EVENT_LABELS') {
    return {
      latest: {
        platform: 'rumble',
        eventType: 'follow',
        displayName: 'Kestrel',
        avatarUrl: null,
        amount: null,
        currency: null,
        displayAmount: null,
        occurredAt: now,
      },
    };
  }

  return {
    items: [
      { platform: 'twitch', eventType: 'subscription', displayName: 'Kestrel', avatarUrl: null, amount: null, currency: null, displayAmount: null, occurredAt: now },
      { platform: 'youtube', eventType: 'donation', displayName: 'a_very_long_viewer_handle_indeed', avatarUrl: null, amount: 25, currency: 'USD', displayAmount: '$25.00', occurredAt: now },
      { platform: 'rumble', eventType: 'follow', displayName: 'Mo', avatarUrl: null, amount: null, currency: null, displayAmount: null, occurredAt: now },
    ],
  };
}

export function WidgetPreview({
  widgetType,
  config,
  state,
  backdrop,
  compact = false,
}: {
  widgetType: string;
  config: Record<string, unknown> | null;
  state?: Record<string, unknown> | undefined;
  backdrop: Backdrop;
  /** Gallery cards draw smaller and do not scroll. */
  compact?: boolean;
}): React.JSX.Element {
  return (
    <div
      className={`wpreview wpreview--${backdrop}${compact ? ' wpreview--compact' : ''}`}
      /*
       * Decorative as a whole: the widget inside carries the real content and
       * its own semantics, and announcing "preview image" over it would put a
       * label in front of text a screen reader can already read.
       */
      data-backdrop={backdrop}
    >
      <div className="wpreview__stage">
        <WidgetRender
          widgetType={widgetType}
          config={config}
          state={state ?? previewState(widgetType)}
        />
      </div>
    </div>
  );
}

/**
 * The backdrop switcher.
 *
 * A segmented control rather than a dropdown: four options an operator will
 * flip between repeatedly while judging readability, and hiding them behind a
 * click would make the comparison they exist for harder than not having them.
 */
export function BackdropPicker({
  value,
  onChange,
}: {
  value: Backdrop;
  onChange: (next: Backdrop) => void;
}): React.JSX.Element {
  return (
    <div className="segmented" role="group" aria-label="Preview background">
      {BACKDROPS.map((option) => (
        <button
          key={option}
          type="button"
          className={`segmented__option${value === option ? ' segmented__option--active' : ''}`}
          aria-pressed={value === option}
          onClick={() => {
            onChange(option);
          }}
        >
          {BACKDROP_LABELS[option]}
        </button>
      ))}
    </div>
  );
}

export function useBackdrop(initial: Backdrop = 'grid'): [Backdrop, (next: Backdrop) => void] {
  const [backdrop, setBackdrop] = useState<Backdrop>(initial);
  return [backdrop, setBackdrop];
}

/**
 * One synthetic chat message, for the editor's test control (§103.12).
 *
 * ==================== THIS REACHES NOTHING ====================
 * It is a value returned to a React state setter. It is not published, not
 * persisted, not evaluated by Alerts, Bot or Moderator, and it never enters a
 * workspace's real history. The guarantee is structural: this module imports
 * no client and names no route, and `WidgetPreview.test.tsx` reads the file
 * off disk to prove it — which is why that test just failed on this very
 * comment mentioning the forbidden identifiers in prose.
 * ==============================================================
 */
const SAMPLE_LINES: readonly string[] = [
  'that was a clean run',
  'first!',
  'how long have you been playing this one?',
  'o7',
  'chat is going to lose it',
  'the pathfinding on that boss is broken lol',
];

const SAMPLE_NAMES: readonly string[] = [
  'Kestrel',
  'Mo',
  'quietriver',
  'a_very_long_viewer_handle_indeed',
  'ぷらいむ',
];

export function syntheticMessage(platform: string): Record<string, unknown> {
  const pick = <T,>(list: readonly T[]): T =>
    list[Math.floor(Math.random() * list.length)] as T;

  return {
    // Unique per injection, so the renderer's dedupe does not swallow the
    // second click of the same button.
    id: `sim-${String(Date.now())}-${String(Math.random()).slice(2, 8)}`,
    platform,
    displayName: pick(SAMPLE_NAMES),
    text: pick(SAMPLE_LINES),
    badges: Math.random() > 0.6 ? [{ id: 'subscriber', label: 'SUB' }] : [],
    avatarUrl: null,
    occurredAt: new Date().toISOString(),
  };
}
